import { useMemo } from 'react';
import type { AlignmentStatus, Metro } from '../types/metro';
import { getSectorData } from '../lib/map-utils';

/**
 * Per-metro derived metrics shown in the rankings table — computed
 * frontend-side from the existing `getSectorData()` deterministic
 * sector breakdown (the same data the map popup already uses).
 *
 * When Georgetown ships a real `/api/metros/<id>/sectors` endpoint
 * these values get sourced from there and `getSectorData()` retires.
 *
 * RANKINGS-MARIA-fix-1 (2026-05-07): Maria flagged that picking an
 * Occupational Group on the rankings page didn't visibly change
 * anything. The dropdown WAS wired (it narrowed rows by ~30% via
 * `useFilteredMetros`), but the per-row VALUES (severity ratio,
 * shortage share, most-underproduced) all stayed metro-wide. Result:
 * the dropdown felt no-op even though it wasn't.
 *
 * Fix: the metrics hook now optionally takes a `lensSectorName`
 * (e.g. `'STEM'`). When set, each metric carries a sector-specific
 * `lensRatio` + `lensStatus` so the rendering layer can swap in the
 * sector view of the data. The metro-wide fields stay so legacy
 * rendering paths keep working.
 *
 * RANKINGS-POLISH (2026-05-07, code-reviewer suggestion #3): the
 * lens lookup is now a name-keyed Map built once per `getSectorData()`
 * result, so the per-metro cost of the lens is O(1) lookup instead
 * of an O(N) `Array.find()`. At 5 sectors today this is irrelevant,
 * but Maria's ask #2 (real Excel ingest) is going to land 22
 * occupational groups + 159 occupations — at that point the linear
 * scan becomes a real cost on every filter recompute. Cheap to fix
 * now; expensive to fix later when other code paths depend on the
 * same `getSectorData()` shape.
 */
export interface RankingMetric {
  /** Number of springboard sector breakdowns recorded for this metro. */
  readonly springboardCount: number;
  /** Share of springboard sectors in shortage (0..1). */
  readonly shortageShare: number;
  /**
   * CR-016: Georgetown's tie-broken "largest shortage" occupational group
   * for the metro (`metro.maxShortageOccGroup`). Was the alphabetical-first
   * of the min-ratio groups; now the supplied value. `''` if a legacy
   * fixture omits the field.
   */
  readonly mostUnderproducedGroup: string;
  /**
   * When a lens sector is active, the credentials-to-jobs ratio for
   * THAT sector in this metro. `undefined` when no lens — callers
   * fall back to the metro-wide ratio (`metro.ratio`).
   */
  readonly lensRatio?: number;
  /**
   * When a lens sector is active, the alignment status for THAT
   * sector in this metro. `undefined` when no lens — callers fall
   * back to the metro-wide status (`metro.status`).
   */
  readonly lensStatus?: AlignmentStatus;
  /**
   * When a lens sector is active: Georgetown's supplied annual credentials
   * awarded (`awardsSb`) for that sector in this metro, read straight from
   * `lensSector.credentials`. CR-013: this used to be reconstructed as
   * `Math.round(jobs * ratio)`, which amplified the rounded ratio into a
   * wrong whole-credentials figure — the discrepancy Emma reported. Never
   * reconstruct; read the supplied column.
   * 2026-05-18 feedback added this so the Rankings table can swap to
   * "Number of credentials / Number of jobs / Ratio / Shortage"
   * columns when an occupational group is selected.
   */
  readonly lensCredentials?: number;
  /** When a lens sector is active: number of jobs in that sector for this metro. */
  readonly lensJobs?: number;
  /**
   * When a lens sector is active: Georgetown's supplied annual credential
   * shortage for that sector, read straight from `lensSector.shortage`.
   * CR-013: never `max(0, jobs - credentials)` — that is our definition,
   * not Georgetown's supplied `shortage` column.
   */
  readonly lensShortage?: number;
}

/**
 * Compute the rankings-table metrics for a list of metros. Memoized
 * by reference identity of the metros array so re-renders during
 * filter/sort flips don't re-walk every sector for every metro.
 *
 * Returns a Map keyed by metro id so callers can do a single O(1)
 * lookup per row.
 *
 * @param metros - The metros to compute metrics for.
 * @param lensSectorName - Optional SECTOR_META display name to lens
 *   through (e.g. `'STEM'`, `'Health'`). When set, every returned
 *   metric carries `lensRatio` + `lensStatus` reflecting that
 *   sector's view of the metro. When null/undefined, those fields
 *   are absent.
 */
export function useRankingMetrics(
  metros: ReadonlyArray<Metro>,
  lensSectorName?: string | null,
): ReadonlyMap<string, RankingMetric> {
  return useMemo(() => {
    const out = new Map<string, RankingMetric>();
    for (const metro of metros) {
      const sectors = getSectorData(metro);
      const shortageCount = sectors.filter((s) => s.status === 'shortage').length;
      // CR-016: the "largest shortage" group is Georgetown's tie-broken
      // supplied value (metro.maxShortageOccGroup), NOT the alphabetical
      // first of the min-ratio groups. The old `sectors.reduce()` min-ratio
      // scan kept the FIRST element on a tie (alphabetical, per the ingest
      // sort) — meaningless when two groups tie at ratio 0. Read the
      // supplied field; the backend always populates it for real data.
      // RANKINGS-POLISH suggestion #3: name-keyed Map lookup instead
      // of `sectors.find()`. Avoids the linear scan that becomes
      // material once Maria's ask #2 expands the sector set from 5
      // to 22+. Built per-metro because the sector array identity
      // differs per metro (each call to `getSectorData` allocates
      // a fresh array); keying by sector name is the cheapest way
      // to share the lens lookup with future `mostUnderproducedGroup`
      // refactors that may want sector access too.
      const sectorByName = lensSectorName
        ? new Map(sectors.map((s) => [s.name, s]))
        : null;
      const lensSector = sectorByName?.get(lensSectorName as string);
      out.set(metro.id, {
        springboardCount: sectors.length,
        shortageShare: sectors.length === 0 ? 0 : shortageCount / sectors.length,
        mostUnderproducedGroup: metro.maxShortageOccGroup ?? '',
        ...(lensSector
          ? {
              lensRatio: lensSector.ratio,
              lensStatus: lensSector.status,
              // CR-013: read Georgetown's supplied figures straight from the
              // sector row — never reconstruct from jobs/ratio. `?? undefined`
              // keeps the field absent in the seed-math fallback (null),
              // where callers fall back to 0 / an em-dash.
              lensCredentials: lensSector.credentials ?? undefined,
              lensJobs: lensSector.jobs,
              lensShortage: lensSector.shortage ?? undefined,
            }
          : {}),
      });
    }
    return out;
  }, [metros, lensSectorName]);
}
